import { useState } from "react";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Search, Zap, CheckCircle2, XCircle, Radar, BookOpen, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

/* ---------------- Guide: in-app runbook, right-side panel like UCRM's field help ---------------- */
function Code({ children }) {
  return (
    <pre className="bg-[#0a0a0b] text-[#e6e8eb] border border-nhp-border rounded-none p-3 font-mono text-[11px] leading-relaxed overflow-x-auto my-2">
      {children}
    </pre>
  );
}
function GuideSection({ title, children }) {
  return (
    <div className="mb-7">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-primary mb-2">{title}</div>
      <div className="space-y-2 text-sm text-nhp-text leading-relaxed">{children}</div>
    </div>
  );
}
function Callout({ tone = "warning", children }) {
  const boxCls = tone === "critical"
    ? "border-nhp-critical/30 bg-nhp-critical/5 border-l-nhp-critical"
    : "border-nhp-warning/30 bg-nhp-warning/5 border-l-nhp-warning";
  const labelCls = tone === "critical" ? "text-nhp-critical" : "text-nhp-warning";
  return (
    <div className={`border border-l-4 p-3 text-xs ${boxCls}`}>
      <div className={`flex items-center gap-1.5 font-mono uppercase tracking-[0.1em] mb-1 ${labelCls}`}>
        <AlertTriangle className="h-3 w-3" /> {tone === "critical" ? "Can't be done remotely" : "Not automated yet"}
      </div>
      <div className="text-nhp-muted leading-relaxed">{children}</div>
    </div>
  );
}

function ProvisioningGuide({ open, onClose }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-md bg-nhp-surface border-l border-nhp-border h-full overflow-y-auto p-6">
        <div className="flex items-start justify-between mb-1">
          <h2 className="font-heading font-black text-xl flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-nhp-primary" /> Provisioning guide
          </h2>
          <button onClick={onClose} className="text-nhp-muted hover:text-nhp-text">
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="text-xs text-nhp-muted mb-6">Written against RouterOS 7.19.6 on a hEX S. The steps below run in the same order as the form on the left.</p>

        <GuideSection title="// How it fits together">
          <p>One <strong>core router</strong> holds the WireGuard tunnel back to this server and acts as a PPPoE concentrator. A new site router doesn't need its own tunnel — it just needs real internet access to dial PPPoE out to the core router's public IP, which already has the path back here.</p>
          <p>Each site gets its own subnet (10.90.x, 10.91.x, 10.92.x…) — that's how payments get routed to the correct router automatically.</p>
        </GuideSection>

        <GuideSection title="// Step 1 — wire it up + dial in (manual)">
          <p>Plug the internet uplink into <code className="text-nhp-primary">ether1</code> — the WAN port on hEX/hAP hardware by default. In a WinBox terminal:</p>
          <Code>{`/interface pppoe-client
add name=pppoe-wan interface=ether1 user=<site user> \\
    password=<site password> add-default-route=yes \\
    use-peer-dns=yes disabled=no`}</Code>
          <p>Confirm it connected before using Look Up IP:</p>
          <Code>{`/interface pppoe-client monitor pppoe-wan`}</Code>
          <p>Look for <code className="text-nhp-primary">status: connected</code>. This is only to bootstrap reachability — provisioning will reconfigure this interface properly (and leaves it alone if it's already correct).</p>
        </GuideSection>

        <GuideSection title="// Steps 1–3 on the left — what each does">
          <p><strong>Look Up IP</strong> asks the core router for the address it just assigned this PPPoE session.</p>
          <p><strong>Detect Router</strong> connects to that address and reads back its interfaces, so you confirm the WAN/LAN split before anything is written.</p>
          <p><strong>Provision Router</strong> pushes the full config in one pass: LAN bridge, PPPoE uplink, DHCP, hotspot server + profile, RADIUS client, walled garden, management-tunnel firewall exemptions, and the routes that make the new subnet reachable from here going forward.</p>
        </GuideSection>

        <GuideSection title="// Device Mode unlock">
          <Callout tone="critical">
            Newer hardware (confirmed on hEX S) ships in a reduced "home" license mode with hotspot, proxy, and several other features disabled — a licensing restriction, not a bug. Provisioning reports success even though hotspot never actually turns on. Confirming the change needs an interactive yes/no prompt, which the API can't answer — this has to be run at the router.
          </Callout>
          <Code>{`/system/device-mode/update hotspot=yes
# "...will reboot the RouterOS, continue? [y/N]" -> y

# after reboot, confirm:
/system/device-mode/print   # look for hotspot: true`}</Code>
        </GuideSection>

        <GuideSection title="// Known gaps to check after reboot">
          <Callout>
            Hotspot server may still be disabled (RouterOS auto-disabled it under the restriction, unlocking Device Mode doesn't flip it back):
          </Callout>
          <Code>{`/ip hotspot set [find] disabled=no`}</Code>
          <Callout>DNS remote requests, missing from the automated DNS step:</Callout>
          <Code>{`/ip dns set allow-remote-requests=yes`}</Code>
          <Callout>
            No masquerade rule on some hardware (symptom: portal loads, but no real internet after paying — "webpage not available" for every destination):
          </Callout>
          <Code>{`/ip firewall nat print   # chain=srcnat should NOT be empty
/ip firewall nat add chain=srcnat action=masquerade \\
    out-interface-list=WAN comment=NHP-masquerade`}</Code>
          <Callout>
            Custom captive portal page — without it, hotspot works but shows MikroTik's stock login page. <code className="text-nhp-primary">/tool/fetch</code> is also Device-Mode-gated, so push it over FTP instead:
          </Callout>
          <Code>{`curl -T login.html --ftp-pasv -u "admin:<password>" \\
    "ftp://<router-ip>/flash/hotspot/login.html"`}</Code>
          <Code>{`<html><head>
<meta http-equiv="refresh" content="0;url=https://hotspot.netonlain.online/?mac=$(mac)&ip=$(ip)">
<script>window.location.replace("https://hotspot.netonlain.online/?mac=$(mac)&ip=$(ip)");</script>
</head><body>Redirecting to NetOnLain…</body></html>`}</Code>
          <p>Leave <code className="text-nhp-primary">$(mac)</code> / <code className="text-nhp-primary">$(ip)</code> exactly as-is — RouterOS fills them in per request.</p>
        </GuideSection>

        <GuideSection title="// Verify end to end">
          <ul className="list-disc list-inside space-y-1 text-nhp-muted">
            <li>Router shows <span className="text-nhp-text">online</span> on the Routers page with a hotspot subnet recorded</li>
            <li><code className="text-nhp-primary">/ip hotspot print</code> shows the server running, not disabled</li>
            <li><code className="text-nhp-primary">/ip firewall nat print</code> shows real packet counters once a device connects</li>
            <li>A test device sees NHP's actual portal, not MikroTik's stock page</li>
            <li>A real test payment results in actual internet access</li>
          </ul>
        </GuideSection>

        <GuideSection title="// Troubleshooting">
          <table className="w-full text-xs">
            <tbody className="divide-y divide-nhp-border">
              <tr><td className="py-2 pr-2 text-nhp-muted">Stock MikroTik login page</td><td className="py-2">login.html never pushed</td></tr>
              <tr><td className="py-2 pr-2 text-nhp-muted">Portal loads, no internet after paying</td><td className="py-2">missing masquerade rule</td></tr>
              <tr><td className="py-2 pr-2 text-nhp-muted">Nothing hotspot-related works at all</td><td className="py-2">Device Mode still blocking hotspot</td></tr>
              <tr><td className="py-2 pr-2 text-nhp-muted">One payment, 2–3 subscriptions</td><td className="py-2">callback was slow (often a dead router still marked online), M-Pesa retried the webhook</td></tr>
              <tr><td className="py-2 pr-2 text-nhp-muted">Paid customer asked to pay again elsewhere</td><td className="py-2">that router was offline when the bypass fan-out ran</td></tr>
            </tbody>
          </table>
        </GuideSection>
      </div>
    </div>
  );
}

const emptyConn = { ip_address: "", api_port: 8728, mikrotik_user: "admin", mikrotik_password: "" };
const emptySite = { site_name: "", site_number: "", pppoe_user: "", pppoe_password: "", radius_secret: "" };

export default function ProvisionSite() {
  const [guideOpen, setGuideOpen] = useState(false);
  const [conn, setConn] = useState(emptyConn);
  const [site, setSite] = useState(emptySite);
  const [detecting, setDetecting] = useState(false);
  const [applying, setApplying] = useState(false);
  const [detected, setDetected] = useState(null);
  const [wanInterface, setWanInterface] = useState("");
  const [lanInterfaces, setLanInterfaces] = useState([]);
  const [result, setResult] = useState(null);
  const [lookingUp, setLookingUp] = useState(false);

  const lookupPppoe = async () => {
    if (!site.pppoe_user) {
      toast.error("Enter the PPPoE username first");
      return;
    }
    setLookingUp(true);
    try {
      const { data } = await api.post("/admin/provisioning/lookup-pppoe", { pppoe_user: site.pppoe_user });
      if (!data.configured) {
        toast.error("Core router credentials aren't configured on the backend yet");
      } else if (!data.connected) {
        toast.error("That PPPoE username isn't dialed in yet — make sure the router is plugged in and its PPPoE credentials are set");
      } else {
        setConn((c) => ({ ...c, ip_address: data.address }));
        toast.success(`Found it — dialed in at ${data.address}`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLookingUp(false);
    }
  };

  const detect = async () => {
    if (!conn.ip_address || !conn.mikrotik_password) {
      toast.error("Enter the router's IP and admin password first");
      return;
    }
    setDetecting(true);
    setDetected(null);
    setResult(null);
    try {
      const { data } = await api.post("/admin/provisioning/detect", conn);
      setDetected(data);
      setWanInterface(data.wanCandidate || "");
      setLanInterfaces(data.lanCandidates || []);
      toast.success(`Found ${data.boardName} (RouterOS ${data.routerosVersion})`);
      if (data.hotspotRestricted) {
        toast.warning("This router's hotspot feature is locked by Device Mode — see the Guide for the unlock steps (needs a local terminal, can't be done from here).", { duration: 8000 });
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setDetecting(false);
    }
  };

  const toggleLan = (iface) => {
    setLanInterfaces((cur) => (cur.includes(iface) ? cur.filter((i) => i !== iface) : [...cur, iface]));
  };

  const apply = async () => {
    if (!site.site_name || !site.site_number || !site.pppoe_user || !site.pppoe_password || !site.radius_secret) {
      toast.error("Fill in all site fields first");
      return;
    }
    if (!wanInterface || lanInterfaces.length === 0) {
      toast.error("Choose a WAN interface and at least one LAN interface");
      return;
    }
    setApplying(true);
    try {
      const { data } = await api.post("/admin/provisioning/apply", {
        ...conn, ...site,
        wan_interface: wanInterface, lan_interfaces: lanInterfaces,
      });
      setResult(data);
      toast.success(`Site "${site.site_name}" provisioned`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setApplying(false);
    }
  };

  return (
    <div>
      <PageHeader
        testId="provision-header"
        title="Provision Site"
        subtitle="Detect a freshly-connected router and push the full NHP hotspot config to it over the RouterOS API"
        actions={
          <Button
            type="button" variant="outline" data-testid="provision-guide-btn"
            onClick={() => setGuideOpen(true)}
            className="h-9 rounded-none border-nhp-border text-nhp-muted hover:text-nhp-text font-mono text-xs"
          >
            <BookOpen className="h-4 w-4 mr-2" /> GUIDE
          </Button>
        }
      />
      <ProvisioningGuide open={guideOpen} onClose={() => setGuideOpen(false)} />

      <div className="p-4 md:p-8 max-w-3xl space-y-6">
        {/* Step 1: connection */}
        <div className="border border-nhp-border bg-nhp-surface p-4 md:p-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-primary mb-4">// Step 1 — Connect</div>

          <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-4 pb-4 border-b border-nhp-border">
            <div className="flex-1">
              <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">PPPoE Username (already dialed in?)</Label>
              <Input data-testid="prov-pppoe-lookup-user" value={site.pppoe_user} onChange={(e) => setSite({ ...site, pppoe_user: e.target.value })} className="mt-1 bg-nhp-bg border-nhp-border rounded-none h-10 font-mono" placeholder="nhp-site2" />
            </div>
            <Button type="button" variant="outline" onClick={lookupPppoe} disabled={lookingUp} className="h-10 rounded-none border-nhp-border text-nhp-muted hover:text-nhp-text font-mono text-xs shrink-0">
              {lookingUp ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
              LOOK UP IP
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Router IP (PPPoE-assigned)</Label>
              <Input data-testid="prov-ip" value={conn.ip_address} onChange={(e) => setConn({ ...conn, ip_address: e.target.value })} className="mt-1 bg-nhp-bg border-nhp-border rounded-none h-10 font-mono" placeholder="10.60.254.2" />
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">API Port</Label>
              <Input type="number" value={conn.api_port} onChange={(e) => setConn({ ...conn, api_port: +e.target.value })} className="mt-1 bg-nhp-bg border-nhp-border rounded-none h-10 font-mono" />
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Admin User</Label>
              <Input value={conn.mikrotik_user} onChange={(e) => setConn({ ...conn, mikrotik_user: e.target.value })} className="mt-1 bg-nhp-bg border-nhp-border rounded-none h-10 font-mono" />
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Admin Password</Label>
              <Input type="password" value={conn.mikrotik_password} onChange={(e) => setConn({ ...conn, mikrotik_password: e.target.value })} className="mt-1 bg-nhp-bg border-nhp-border rounded-none h-10 font-mono" />
            </div>
          </div>
          <Button data-testid="prov-detect-btn" onClick={detect} disabled={detecting} className="mt-4 h-10 rounded-none bg-nhp-primary hover:bg-nhp-primary-hover text-nhp-on-primary font-bold">
            {detecting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
            DETECT ROUTER
          </Button>
        </div>

        {/* Step 2: review */}
        {detected && (
          <div className="border border-nhp-border bg-nhp-surface p-4 md:p-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-primary mb-4">// Step 2 — Review hardware</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
              <div className="border border-nhp-border p-3">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Board</div>
                <div className="font-mono text-sm mt-1">{detected.boardName}</div>
              </div>
              <div className="border border-nhp-border p-3">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">RouterOS</div>
                <div className="font-mono text-sm mt-1">{detected.routerosVersion}</div>
              </div>
              <div className="border border-nhp-border p-3">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Onboard WiFi</div>
                <div className="font-mono text-sm mt-1">{detected.hasWireless ? "Yes (unused by NHP)" : "None"}</div>
              </div>
            </div>

            <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">WAN / PPPoE Uplink Port</Label>
            <div className="flex flex-wrap gap-2 mt-2 mb-4">
              {detected.interfaces.map((iface) => (
                <button
                  key={iface}
                  type="button"
                  onClick={() => setWanInterface(iface)}
                  className={`font-mono text-xs px-3 py-1.5 border ${wanInterface === iface ? "border-nhp-primary text-nhp-primary bg-nhp-primary/10" : "border-nhp-border text-nhp-muted hover:text-nhp-text"}`}
                >
                  {iface}
                </button>
              ))}
            </div>

            <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">
              LAN / Hotspot Ports (auto-selected — uncheck any reserved for backhaul)
            </Label>
            <div className="space-y-2 mt-2">
              {detected.interfaces.filter((i) => i !== wanInterface).map((iface) => (
                <label key={iface} className="flex items-center gap-2 text-sm font-mono">
                  <Checkbox checked={lanInterfaces.includes(iface)} onCheckedChange={() => toggleLan(iface)} />
                  {iface}
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: site details */}
        {detected && (
          <div className="border border-nhp-border bg-nhp-surface p-4 md:p-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-primary mb-4">// Step 3 — Site details</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Site Name</Label>
                <Input data-testid="prov-site-name" value={site.site_name} onChange={(e) => setSite({ ...site, site_name: e.target.value })} className="mt-1 bg-nhp-bg border-nhp-border rounded-none h-10" placeholder="Site 2 — Kericho" />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">Site Number</Label>
                <Input type="number" data-testid="prov-site-number" value={site.site_number} onChange={(e) => setSite({ ...site, site_number: e.target.value })} className="mt-1 bg-nhp-bg border-nhp-border rounded-none h-10 font-mono" placeholder="2" />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">PPPoE Username</Label>
                <div className="mt-1 h-10 flex items-center px-3 border border-nhp-border bg-nhp-bg font-mono text-sm text-nhp-muted">{site.pppoe_user || "— set in Step 1 —"}</div>
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">PPPoE Password</Label>
                <Input type="password" value={site.pppoe_password} onChange={(e) => setSite({ ...site, pppoe_password: e.target.value })} className="mt-1 bg-nhp-bg border-nhp-border rounded-none h-10 font-mono" />
              </div>
              <div className="col-span-2">
                <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-muted">RADIUS Secret</Label>
                <Input type="password" value={site.radius_secret} onChange={(e) => setSite({ ...site, radius_secret: e.target.value })} className="mt-1 bg-nhp-bg border-nhp-border rounded-none h-10 font-mono" />
              </div>
            </div>
            {site.site_number && (
              <div className="mt-4 font-mono text-xs text-nhp-muted">
                Management subnet <span className="text-nhp-text">192.168.{49 + Number(site.site_number)}.0/24</span> · Hotspot subnet <span className="text-nhp-text">10.{89 + Number(site.site_number)}.0.0/24</span>
              </div>
            )}
            <Button data-testid="prov-apply-btn" onClick={apply} disabled={applying} className="mt-5 h-11 rounded-none bg-nhp-primary hover:bg-nhp-primary-hover text-nhp-on-primary font-bold">
              {applying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Zap className="h-4 w-4 mr-2" />}
              PROVISION ROUTER
            </Button>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="border border-nhp-border bg-nhp-surface p-4 md:p-6">
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-nhp-primary mb-4">
              <CheckCircle2 className="h-4 w-4" /> Provisioned successfully
            </div>
            <div className="space-y-1">
              {result.log?.map((step, i) => (
                <div key={i} className="flex items-center gap-2 text-xs font-mono">
                  {step.ok ? <CheckCircle2 className="h-3.5 w-3.5 text-nhp-primary shrink-0" /> : <XCircle className="h-3.5 w-3.5 text-nhp-critical shrink-0" />}
                  <span className={step.ok ? "text-nhp-muted" : "text-nhp-critical"}>{step.label}{step.error ? ` — ${step.error}` : ""}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-nhp-border font-mono text-xs text-nhp-muted">
              Router registered as <span className="text-nhp-text">{result.router?.name}</span> under site <span className="text-nhp-text">{result.site?.name}</span>.
              Reachable at <span className="text-nhp-text">{conn.ip_address}</span> — this address is already routed through the existing WireGuard tunnel, so no further network changes are needed for the backend to manage it.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
